import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useProjects } from "@/context/ProjectContext";
import SubmissionForm from "@/components/submissions/SubmissionForm";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Home } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { projectApi } from "@/services/projectService";

const EditSubmission = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { getProjectById } = useProjects();

  const project = projectId ? getProjectById(projectId) : null;

  const {
    data: projectDetails,
    isLoading,
    isError,
    status,
  } = useQuery({
    queryKey: ["projects", projectId],
    queryFn: () => projectApi.getProject(projectId),
    enabled: !!projectId && !!isAuthenticated,
    retry: 0,
  });

  if (!project) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Button
            variant="outline"
            className="mr-4"
            onClick={() => navigate("/projects")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
          <h1 className="text-3xl font-bold">Project Not Found</h1>
        </div>

        <Card>
          <CardContent className="py-8 text-center">
            <p>
              The project you're trying to submit a permit for could not be
              found.
            </p>
            <Button className="mt-4" onClick={() => navigate("/projects")}>
              View All Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-8 space-y-8 animate-in fade-in duration-500">
      {/* Breadcrumb */}
      <nav
        className="flex items-center text-sm text-muted-foreground mb-2"
        aria-label="Breadcrumb"
      >
        <Link
          to="/"
          className="hover:text-primary transition-colors flex items-center"
        >
          <Home className="h-4 w-4 mr-1" />
        </Link>
        <span className="mx-2 text-border">/</span>
        <Link
          to="/projects"
          className="hover:text-primary transition-colors"
        >
          Projects
        </Link>
        <span className="mx-2 text-border">/</span>
        <Link
          to={`/projects/${projectId}`}
          className="hover:text-primary transition-colors"
        >
          {project.name}
        </Link>
        <span className="mx-2 text-border">/</span>
        <span className="text-foreground font-medium">Edit Submission</span>
      </nav>

      <Card className="border-border/50 shadow-sophisticated highlight-top overflow-hidden">
        <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
          <CardTitle className="text-xl">Edit Permit Submission</CardTitle>
          <CardDescription>
            Please complete the form below with details about your high-pile
            storage system. All fields marked with * are required.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-8">
          <SubmissionForm
            projectId={projectId}
            projectDetails={projectDetails}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default EditSubmission;
